import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/apiClient";
import BrandLogo from "@/components/BrandLogo";
import VehicleTimeline from "@/components/VehicleTimeline";
import { Car, CheckCircle2, Wrench, ChevronRight, ShieldCheck, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  entrou: "Veículo recebido",
  em_inspecao: "Em inspeção",
  aguardando_estimate: "Preparando orçamento",
  aguardando_aprovacao: "Aguardando sua aprovação",
  aguardando_pecas: "Aguardando peças",
  em_andamento: "Em execução",
  pronto: "Pronto para retirada",
  entregue: "Entregue",
  // Body shop
  novo: "Veículo recebido",
  aprovado: "Aprovado",
  em_reparo: "Em reparo",
  pintura: "Pintura",
  montagem: "Montagem",
  quality_check: "Controle de qualidade",
  pronto_entrega: "Pronto para retirada",
  // Warranty
  recebido: "Recebido",
  estimate: "Estimate garantia",
  kit_enviado: "Kit enviado",
  agendado: "Agendado",
  aguardando_pagamento: "Aguardando pagamento",
};

export default function ClientePortal() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/portal/${token}`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else if (!silent) toast.error("Erro ao carregar portal");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh a cada 60s para status sempre atualizado sem forçar reload manual
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, [token]);

  if (loading) {
    return (
      <PortalShell>
        <div className="text-sm text-muted-foreground py-20 text-center">Carregando…</div>
      </PortalShell>
    );
  }

  if (notFound) {
    return (
      <PortalShell>
        <div className="text-center py-20">
          <div className="font-display text-2xl font-bold">Link não encontrado</div>
          <div className="text-sm text-muted-foreground mt-2">Verifique o endereço com a oficina.</div>
        </div>
      </PortalShell>
    );
  }

  const { client, vehicles, jobs, brand } = data;
  // Mantém apenas a OS mais recente por veículo (jobs já vêm ordenados desc por created_at do backend).
  const latestPerVehicle = (() => {
    const seen = new Set();
    const out = [];
    for (const j of jobs) {
      if (!seen.has(j.vehicle_id)) { out.push(j); seen.add(j.vehicle_id); }
    }
    return out;
  })();
  const activeJobs = latestPerVehicle.filter((j) => !["entregue", "aguardando_pagamento"].includes(j.status));
  const awaitingApproval = activeJobs.filter((j) => j.status === "aguardando_aprovacao").length;

  return (
    <PortalShell brand={brand}>
      <div className="space-y-6" data-testid="portal-home">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Portal exclusivo</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Olá, {client.name.split(" ")[0]}.
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Acompanhe em tempo real o andamento de cada veículo dentro da oficina.
            </p>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 self-start md:self-auto"
            data-testid="portal-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </div>

        {/* Alerta de aprovações */}
        {awaitingApproval > 0 && (
          <div
            className="border border-orange-500/40 bg-orange-500/10 rounded-md p-4 flex items-start gap-3"
            data-testid="approval-banner"
          >
            <ShieldCheck className="w-5 h-5 text-orange-500 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold text-orange-500">
                {awaitingApproval} serviço(s) aguardando sua aprovação
              </div>
              <div className="text-sm text-muted-foreground">
                Toque no card do veículo abaixo para revisar e aprovar.
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <PortalKpi icon={Car} label="Na Oficina" value={activeJobs.length} testId="kpi-ativos" />
          <PortalKpi icon={Wrench} label="Aprovações" value={awaitingApproval} tone={awaitingApproval > 0 ? "text-orange-500" : ""} testId="kpi-aprovacoes" />
          <PortalKpi icon={CheckCircle2} label="Concluídos" value={doneJobs.length} tone="text-emerald-500" testId="kpi-concluidos" />
        </div>

        {/* ATIVOS — jornada de cada veículo */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold tracking-tight">Veículos atualmente na oficina</h2>
          </div>
          {activeJobs.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-10 text-center" data-testid="empty-active">
              <Car className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm font-medium">Nenhum veículo em atendimento agora.</div>
              <div className="text-xs text-muted-foreground mt-1">
                Quando um dos seus veículos entrar na oficina, ele aparecerá aqui.
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {activeJobs.map((j) => (
                <ActiveVehicleCard
                  key={j.id}
                  job={j}
                  onClick={() => navigate(`/portal/${token}/job/${j.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* HISTÓRICO — veículos entregues */}
        {doneJobs.length > 0 && (
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight mb-3">Histórico recente</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {doneJobs.slice(0, 6).map((j) => (
                <div
                  key={j.id}
                  className="border border-border rounded-md bg-card p-3 flex items-center gap-3"
                  data-testid={`portal-history-${j.id}`}
                >
                  <div className="w-12 h-12 rounded bg-secondary shrink-0 overflow-hidden">
                    {j.vehicle?.photo_url ? (
                      <img src={j.vehicle.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">
                      {j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      Entregue · {new Date(j.updated_at).toLocaleDateString("pt-BR")}
                    </div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Todos os veículos (cadastro completo) */}
        {vehicles.length > 0 && (
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight mb-3">Seus veículos cadastrados</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {vehicles.map((v) => (
                <div
                  key={v.id}
                  className="border border-border rounded-md bg-card overflow-hidden"
                  data-testid={`portal-vehicle-${v.id}`}
                >
                  <div className="h-20 bg-secondary">
                    {v.photo_url ? (
                      <img src={v.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="font-semibold text-xs truncate">
                      {v.year} {v.make} {v.model}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono">{v.plate || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  );
}

// ---------- Active vehicle card com timeline animada ----------
function ActiveVehicleCard({ job, onClick }) {
  const statusLbl = STATUS_LABEL[job.status] || job.status;
  const isApproval = job.status === "aguardando_aprovacao";
  return (
    <div
      className="border border-border rounded-xl bg-card overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`portal-active-${job.id}`}
    >
      <button onClick={onClick} className="w-full text-left" data-testid={`portal-active-btn-${job.id}`}>
        <div className="flex flex-col sm:flex-row">
          {/* Foto */}
          <div className="w-full sm:w-40 h-40 sm:h-auto shrink-0 bg-secondary relative">
            {job.vehicle?.photo_url ? (
              <img src={job.vehicle.photo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Car className="w-10 h-10 text-muted-foreground" />
              </div>
            )}
            {isApproval && (
              <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full animate-pulse">
                Ação necessária
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 p-4 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg truncate">
                  {job.vehicle?.year} {job.vehicle?.make} {job.vehicle?.model}
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {job.vehicle?.plate || "—"}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div
                  className={`text-[10px] uppercase tracking-widest font-bold ${
                    isApproval ? "text-orange-500" : "text-primary"
                  }`}
                >
                  {statusLbl}
                </div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-1 justify-end mt-0.5">
                  <Clock className="w-2.5 h-2.5" />
                  {job.days_in_shop} dia{job.days_in_shop !== 1 ? "s" : ""} na oficina
                </div>
              </div>
            </div>
          </div>
        </div>
      </button>

      {/* Timeline animada */}
      <div className="border-t border-border px-3 sm:px-5 bg-secondary/20">
        <VehicleTimeline timeline={job.timeline} status_label={statusLbl} />
      </div>

      {/* Footer com CTA */}
      <button
        onClick={onClick}
        className="w-full border-t border-border py-2.5 flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/5 transition"
        data-testid={`portal-details-btn-${job.id}`}
      >
        Ver detalhes e histórico
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function PortalKpi({ icon: Icon, label, value, tone = "", testId }) {
  return (
    <div className="border border-border rounded-md bg-card p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

export function PortalShell({ children, brand }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between">
          <BrandLogo size="sm" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground text-right">
            <div>Portal do Cliente</div>
            <div className="text-muted-foreground/70">{brand?.fiscal_name || "GM Auto Service"}</div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-5 py-6 sm:py-8">{children}</main>
      <footer className="max-w-4xl mx-auto px-5 py-6 text-[10px] text-muted-foreground/70 uppercase tracking-[0.18em] text-center">
        {brand?.fiscal_note || "Faturamento por GM Auto Service · Operação sob licença AAB Repairs"}
      </footer>
    </div>
  );
}
